import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { mdiAlertCircleOutline } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import ChunkUploadFile from "components/ChunkUploadFile";
import Input from "components/Input";
import { UploadCloudSvgIcon } from "components/SVGIcons";
import Tooltip from "components/Tooltip";
import { CUSTOMER_CATEGORY } from "constants/base";
import { CO_BROKER_ALLOWED_LIST, CREDIT_LIST } from "constants/haulpay";
import { UPLOAD_DOCUMENT_URLS } from "constants/routes";
import { formatNumber } from "helpers/formatNumber";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import { useUserFeature } from "hooks/useUserFeature";
import { ICreditItem } from "models/HaulPay";
import { saveCreditAPI } from "services/api/haulpay";
import { selectUserDetails } from "store/user/selector";
import CardFooter from "./CardFooter";
import CreditStatus from "./CreditStatus";
import styles from "./style.module.scss";

interface IProps {
  data: ICreditItem;
  isEdit?: boolean;
  setSelectedData?: (data: any) => void;
  addCustomer?: (credit_approved: string, id: string) => void;
  goToHistory?: (data: any) => void;
  toggleWarningModal?: VoidFunction;
  toggleNoaStatusModal?: VoidFunction;
}

const getBrokerGridStyle = () => "desktop:grid-cols-8";
const getCarrierGridStyle = () => "desktop:grid-cols-7";

export default function CreditItem({
  data,
  addCustomer,
  setSelectedData,
  goToHistory,
  toggleWarningModal,
  toggleNoaStatusModal,
  isEdit = false
}: Readonly<IProps>) {
  const { isBroker } = useUserFeature();
  const { isDesktop } = useDeviceType();

  const userDetails = useSelector(selectUserDetails);
  const credit = CREDIT_LIST.find((item) => item.value === data.credit_approved)?.label;

  const getGridStyle = () => {
    if (isBroker && userDetails?.allow_co_broker_shipper_debtor) {
      return getBrokerGridStyle();
    } else {
      return getCarrierGridStyle();
    }
  };

  const noaStatus = (): JSX.Element => {
    if (data.noa_active) {
      return <span className="text-green-500">Placed (Active)</span>;
    }
    if (data.noa_sent) {
      return <span className="text-blue-500">Sent (Pending)</span>;
    }
    if (data.noa_requested) {
      return <span className="text-pendingBorder">Request Sent</span>;
    }
    if (data.credit_approved === "approved") {
      return (
        <button
          type="button"
          className="leading-normal text-left text-error hover:underline"
          onClick={() => {
            setSelectedData?.({
              id: data.id,
              relationship_id: data.relationship_id,
              credit_available: data.credit_available,
              dba: data.dba
            });
            toggleNoaStatusModal?.();
          }}>
          Not Sent (click to send)
        </button>
      );
    }
    return <span className="text-error">Not Sent</span>;
  };

  const { register, handleSubmit } = useForm<{
    externalCode: string;
  }>({
    values: {
      externalCode: data.external_code
    }
  });

  const onSubmit = async ({ externalCode }: { externalCode: string }) => {
    const formData = {
      id: data.id,
      external_code: externalCode,
      factoring: userDetails?.factoring_id,
      debtor: data.id,
      co_broker_allowed: data.co_broker_allowed
    };

    try {
      const response = await saveCreditAPI(formData);
      if (response) {
        showSuccessMessage("Debtor has been updated.");
      } else {
        showErrorMessage("Please try again later.");
      }
    } catch (error: any) {
      showErrorMessage(error.message);
    }
  };

  return (
    <form
      className={clsx(styles.cardContainer, isEdit ? "desktop:pb-2" : "desktop:pb-6")}
      onSubmit={handleSubmit(onSubmit)}>
      {isDesktop ? (
        <div className="flex items-center justify-between pl-6 pr-4">
          <ul className="flex flex-col">
            <li className="text-subtitle">DBA</li>
            <li className="font-bold">
              {data.dba}
              {data.address.street_one ? ", " + data.address.street_one : ""}
              {data.address.state ? ", " + data.address.state : ""}
            </li>
          </ul>

          <CardFooter
            data={data}
            addCustomer={addCustomer}
            goToHistory={goToHistory}
            isEdit={isEdit}
            setSelectedData={setSelectedData}
            toggleWarningModal={toggleWarningModal}
          />
        </div>
      ) : null}

      <ul className={clsx(styles.itemContainer, isEdit ? getGridStyle() + " my-5" : "mt-5")}>
        <li className="mobile:!hidden">
          <span className="mobile:font-bold desktop:text-subtitle">Credit</span>
          <CreditStatus credit={credit} credit_approved={data.credit_approved} />
        </li>
        <li className="flex !items-start desktop:!hidden">
          <span className="mobile:font-bold desktop:text-subtitle">DBA</span>
          <span>{data.dba}</span>
        </li>
        <li>
          <span className="mobile:font-bold desktop:text-subtitle">Remaining Credit</span>
          <span>{formatNumber(data.credit_available / 100, 2)}</span>
        </li>
        <li>
          <span className="mobile:font-bold desktop:text-subtitle">Discount Rate</span>
          <span>{data.discount_rate}%</span>
        </li>
        <li className="flex !items-start desktop:!hidden">
          <span className="mobile:font-bold">Address</span>
          <span>{data.address.street_one}</span>
        </li>
        <li className="desktop:!hidden">
          <span className="mobile:font-bold">State</span>
          <span>{data.address.state}</span>
        </li>
        <li>
          <span className="mobile:font-bold desktop:text-subtitle">MC/Dot</span>
          <span>{data.mc}</span>
        </li>
        {isEdit ? (
          <>
            {isBroker && userDetails?.allow_co_broker_shipper_debtor ? (
              <>
                <li className="mobile:py-1 desktop:-ml-10">
                  <span className="mobile:font-bold desktop:text-subtitle">Co Broker Allowed</span>
                  <span>
                    {
                      CO_BROKER_ALLOWED_LIST.find(({ value }) => value === data.co_broker_allowed)
                        ?.label
                    }
                  </span>
                </li>
                <li className="py-1 desktop:!hidden">
                  <span className="mobile:font-bold">Upload Docs</span>

                  <ChunkUploadFile
                    label=""
                    prefixNode={
                      <UploadCloudSvgIcon className="flex-none m-auto text-white size-5" />
                    }
                    className="w-12 h-10 p-2 border-none bg-info"
                    categories={CUSTOMER_CATEGORY}
                    urlCallback={
                      UPLOAD_DOCUMENT_URLS({
                        factoring_id: data.relationship_id
                      }).MY_CUSTOMER
                    }
                    onUploadFinished={() => {}}
                  />
                </li>
              </>
            ) : null}
            <li>
              <span className="mobile:font-bold desktop:text-subtitle">NOA Status</span>
              <span>{noaStatus()}</span>
            </li>
          </>
        ) : null}
        <li>
          <span className="mobile:font-bold desktop:text-subtitle">Status Reason</span>
          {data.credit_approved_note !== "" ? (
            <Tooltip message={data.credit_approved_note}>
              <Icon path={mdiAlertCircleOutline} className="flex-none size-5 text-error" />
            </Tooltip>
          ) : (
            <span>-</span>
          )}
        </li>

        {isEdit ? (
          <>
            <li className="mobile:mt-1">
              <span className="mobile:font-bold desktop:text-subtitle">External Code</span>
              <Input
                name="externalCode"
                register={register}
                className="w-32"
                inputClass="bg-transparent !border !border-subtitle h-10"
              />
            </li>
            <li className="!items-center mt-3.5 desktop:!hidden">
              <span className="mobile:font-bold">Actions</span>
              <div className={styles.actionSection}>
                <button
                  type="button"
                  onClick={() => {
                    goToHistory && goToHistory(data);
                  }}>
                  View History
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedData?.({
                      relationship_id: data.relationship_id,
                      credit_available: data.credit_available,
                      dba: data.dba
                    });
                    toggleWarningModal?.();
                  }}>
                  Request Increase
                </button>
              </div>
            </li>
          </>
        ) : null}
      </ul>

      {!isDesktop ? (
        <CardFooter
          data={data}
          addCustomer={addCustomer}
          goToHistory={goToHistory}
          isEdit={isEdit}
          setSelectedData={setSelectedData}
          toggleWarningModal={toggleWarningModal}
        />
      ) : null}
    </form>
  );
}
